import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolInvoke, type ToolOperation } from '@saas-pages/shared/public/runtime'
import { localeCode, useLocale, useT } from '@/shared/i18n'
import { approvedProductionCalendars, productionCalendarYearRoute } from '@/shared/production-calendar'
import {
  calculateBusinessDays,
  type BusinessDaysCalculation,
  type BusinessDaysResult,
  type CalendarMode,
} from '@/pages/business-days/model/businessDays'
import {
  startFederalCalendarOperation,
} from '@/pages/business-days/model/businessDaysAnalytics'

const UTILITY = 'business-days-calculator'
const DEFAULT_RANGE_DAYS = 29

interface QuickRange {
  key: string
  label: string
  description: string
  startDate: string
  endDate: string
}

function toLocalDate(value: string): Date | null {
  if (!value) return null
  const date = new Date(`${value}T00:00:00`)

  return Number.isNaN(date.getTime()) ? null : date
}

function todayLocalDate(): Date {
  const now = new Date()

  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)

  return next
}

function toInputDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function formatDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

function formatCalendarMonth(date: Date | null, locale: string): string {
  if (!date) return '---'

  return new Intl.DateTimeFormat(locale, { month: 'short' }).format(date)
}

function formatCalendarDay(date: Date | null): string {
  if (!date) return '--'

  return String(date.getDate()).padStart(2, '0')
}

export function BusinessDaysPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeName = localeCode(locale)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [includeEndDate, setIncludeEndDate] = useState(true)
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('weekends_only')
  const federalCalendarOperation = useRef<ToolOperation | null>(null)

  useEffect(() => {
    const today = todayLocalDate()
    setStartDate(toInputDate(today))
    setEndDate(toInputDate(addDays(today, DEFAULT_RANGE_DAYS)))
  }, [])

  const start = useMemo(() => toLocalDate(startDate), [startDate])
  const end = useMemo(() => toLocalDate(endDate), [endDate])
  const result = useMemo<BusinessDaysCalculation | null>(
    () => start && end
      ? calculateBusinessDays(
        startDate,
        endDate,
        includeEndDate,
        calendarMode,
        approvedProductionCalendars,
      )
      : null,
    [calendarMode, end, endDate, includeEndDate, start, startDate],
  )

  useEffect(() => {
    if (calendarMode !== 'ru_federal_five_day' || !result) {
      return
    }

    const operation = federalCalendarOperation.current
    if (!operation) return

    federalCalendarOperation.current = null
    if (result.kind === 'success') {
      operation.success(0)
      return
    }

    operation.error('unsupported_input')
  }, [calendarMode, result])

  useEffect(() => () => {
    federalCalendarOperation.current?.error('cancelled')
  }, [])

  const quickRanges = useMemo<QuickRange[]>(() => {
    const today = todayLocalDate()
    const monday = addDays(today, -((today.getDay() + 6) % 7))

    return [
      {
        key: 'this-week',
        label: t.businessDays.quickThisWeek,
        description: t.businessDays.quickThisWeekHint,
        startDate: toInputDate(monday),
        endDate: toInputDate(addDays(monday, 6)),
      },
      {
        key: 'next-14-days',
        label: t.businessDays.quickTwoWeeks,
        description: t.businessDays.quickTwoWeeksHint,
        startDate: toInputDate(today),
        endDate: toInputDate(addDays(today, 13)),
      },
      {
        key: 'next-30-days',
        label: t.businessDays.quickMonth,
        description: t.businessDays.quickMonthHint,
        startDate: toInputDate(today),
        endDate: toInputDate(addDays(today, 29)),
      },
    ]
  }, [
    t.businessDays.quickMonth,
    t.businessDays.quickMonthHint,
    t.businessDays.quickThisWeek,
    t.businessDays.quickThisWeekHint,
    t.businessDays.quickTwoWeeks,
    t.businessDays.quickTwoWeeksHint,
  ])

  const handleStartChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value)
  }, [])

  const handleEndChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value)
  }, [])

  const handleQuickRange = useCallback((range: QuickRange) => {
    setStartDate(range.startDate)
    setEndDate(range.endDate)
    setIncludeEndDate(true)
    toolInvoke(UTILITY, { action: 'preset', preset: range.key })
  }, [])

  const handleSwap = useCallback(() => {
    setStartDate(endDate)
    setEndDate(startDate)
    toolInvoke(UTILITY, { action: 'swap' })
  }, [endDate, startDate])

  const handleClear = useCallback(() => {
    federalCalendarOperation.current?.error('cancelled')
    federalCalendarOperation.current = null
    setStartDate('')
    setEndDate('')
    setIncludeEndDate(true)
    setCalendarMode('weekends_only')
    toolInvoke(UTILITY, { action: 'reset' })
  }, [])

  const handleCalendarMode = useCallback((mode: CalendarMode) => {
    if (mode === calendarMode) return

    if (mode === 'ru_federal_five_day') {
      federalCalendarOperation.current = startFederalCalendarOperation()
    } else {
      federalCalendarOperation.current?.error('cancelled')
      federalCalendarOperation.current = null
    }
    setCalendarMode(mode)
  }, [calendarMode])

  return (
    <div className="mx-auto max-w-5xl">
      <section className="pb-4 pt-1 sm:pb-8 sm:pt-4">
        <div className="mb-2 inline-flex rounded-md border bg-card px-2 py-0.5 text-xs font-medium text-muted-foreground shadow-sm sm:mb-3 sm:px-2.5 sm:py-1">
          {t.common.localBadge}
        </div>
        <h1 className="text-2xl font-bold tracking-normal sm:text-4xl">
          {t.businessDays.title}
        </h1>
        <p className="mt-1 line-clamp-2 max-w-2xl text-sm leading-6 text-muted-foreground sm:mt-2 sm:line-clamp-none sm:text-base sm:leading-7">
          {t.businessDays.description}
        </p>
      </section>

      <Card className="overflow-hidden">
        <div
          aria-hidden="true"
          className="h-1.5 bg-gradient-to-r from-cyan-400 via-primary to-emerald-400"
        />
        <CardContent className="grid gap-0 p-0 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.95fr)] lg:grid-rows-[auto_1fr]">
          <div className="p-4 sm:p-6 lg:col-start-1 lg:row-start-1 lg:border-r lg:pb-0">
            <div className="grid gap-3 md:grid-cols-[1fr_auto_1fr] md:items-end lg:grid-cols-1 lg:items-stretch">
              <DateField
                id="business-days-start"
                label={t.businessDays.startDate}
                value={startDate}
                date={start}
                locale={localeName}
                onChange={handleStartChange}
              />
              <button
                type="button"
                onClick={handleSwap}
                disabled={!startDate || !endDate}
                className="hidden h-10 w-10 items-center justify-center rounded-md border bg-background text-lg text-muted-foreground shadow-sm transition-colors hover:border-primary/40 hover:text-foreground disabled:pointer-events-none disabled:opacity-50 md:flex lg:hidden"
                aria-label={t.businessDays.swap}
              >
                &#8644;
              </button>
              <DateField
                id="business-days-end"
                label={t.businessDays.endDate}
                value={endDate}
                date={end}
                locale={localeName}
                onChange={handleEndChange}
              />
            </div>
          </div>

          <ResultPanel
            result={result}
            locale={localeName}
            calendarMode={calendarMode}
            onUseWeekendsOnly={() => handleCalendarMode('weekends_only')}
          />

          <div className="space-y-5 border-t p-4 sm:p-6 lg:col-start-1 lg:row-start-2 lg:border-r lg:border-t-0">
            <div>
              <h2 className="mb-2 text-sm font-semibold tracking-normal">
                {t.businessDays.quickRanges}
              </h2>
              <div className="grid gap-2 sm:grid-cols-3">
                {quickRanges.map((range) => (
                  <button
                    key={range.key}
                    type="button"
                    onClick={() => handleQuickRange(range)}
                    className="rounded-md border bg-background/70 px-3 py-2.5 text-left transition-colors hover:border-primary/40 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="block text-sm font-medium">{range.label}</span>
                    <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                      {range.description}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <fieldset>
              <legend className="mb-2 text-sm font-semibold tracking-normal">
                {t.businessDays.calendarMode}
              </legend>
              <div className="grid gap-2">
                {([
                  ['weekends_only', t.businessDays.weekendsOnlyMode],
                  ['ru_federal_five_day', t.businessDays.federalMode],
                ] as const).map(([mode, label]) => (
                  <label
                    key={mode}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 text-sm transition-colors',
                      calendarMode === mode
                        ? 'border-primary/50 bg-primary/10'
                        : 'bg-background/70 hover:border-primary/40',
                    )}
                  >
                    <input
                      type="radio"
                      name="business-days-calendar-mode"
                      value={mode}
                      checked={calendarMode === mode}
                      onChange={() => handleCalendarMode(mode)}
                      className="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="font-medium">{label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <label
              className={cn(
                'flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2.5 text-sm transition-colors',
                includeEndDate
                  ? 'border-primary/50 bg-primary/10'
                  : 'bg-background/70 hover:border-primary/40',
              )}
            >
              <span>
                <span className="block font-medium">{t.businessDays.includeEndDate}</span>
                <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                  {t.businessDays.includeEndDateHint}
                </span>
              </span>
              <input
                type="checkbox"
                checked={includeEndDate}
                onChange={(event) => setIncludeEndDate(event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
            </label>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={handleSwap}
                disabled={!startDate || !endDate}
              >
                {t.businessDays.swap}
              </Button>
              <Button type="button" variant="ghost" onClick={handleClear}>
                {t.common.clear}
              </Button>
            </div>

            <p className="rounded-md border bg-background/70 px-3 py-2 text-xs leading-5 text-muted-foreground">
              {calendarMode === 'weekends_only'
                ? t.businessDays.weekendsOnlyNote
                : t.businessDays.federalModeNote}
            </p>
          </div>
        </CardContent>
      </Card>

      <aside className="mt-6 flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-semibold">{t.businessDays.productionCalendarTitle}</h2>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            {t.businessDays.productionCalendarDescription}
          </p>
        </div>
        <a
          href="/proizvodstvennyj-kalendar"
          className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-md border border-primary/40 bg-background px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-accent"
        >
          {t.businessDays.productionCalendarTitle}
        </a>
      </aside>

      <section className="mt-10 border-t pt-8">
        <h2 className="text-xl font-semibold tracking-normal">
          {t.businessDays.relatedTitle}
        </h2>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {t.businessDays.related.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm transition-colors hover:border-primary/40"
            >
              <h3 className="font-medium">{item.title}</h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {item.description}
              </p>
            </a>
          ))}
        </div>
      </section>

      <section className="mt-10 border-t pt-8">
        <h2 className="text-xl font-semibold tracking-normal">{t.businessDays.faqTitle}</h2>
        <dl className="mt-4 space-y-4">
          {t.businessDays.faq.map((item) => (
            <FaqItem key={item.question} q={item.question} a={item.answer} />
          ))}
        </dl>
      </section>
    </div>
  )
}

function DateField({
  id,
  label,
  value,
  date,
  locale,
  onChange,
}: {
  id: string
  label: string
  value: string
  date: Date | null
  locale: string
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
}) {
  return (
    <label
      htmlFor={id}
      className="block transition-colors focus-within:border-primary/50 sm:rounded-lg sm:border sm:bg-background/70 sm:p-3 sm:shadow-inner"
    >
      <span className="mb-1 block text-sm font-medium sm:mb-3">{label}</span>
      <div className="grid gap-3 sm:grid-cols-[80px_minmax(0,1fr)] sm:items-center">
        <CalendarTile date={date} locale={locale} />
        <input
          id={id}
          type="date"
          value={value}
          onChange={onChange}
          className="h-11 w-full min-w-0 rounded-md border border-input bg-card px-3 pr-10 text-base shadow-sm transition-colors focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring sm:h-10 sm:text-sm"
        />
      </div>
    </label>
  )
}

function CalendarTile({ date, locale }: { date: Date | null; locale: string }) {
  return (
    <span className="hidden overflow-hidden rounded-md border bg-card text-center shadow-sm sm:block">
      <span className="block bg-primary px-2 py-1 text-[0.65rem] font-semibold uppercase tracking-normal text-primary-foreground">
        {formatCalendarMonth(date, locale)}
      </span>
      <span className="block px-3 py-2 text-2xl font-bold leading-none tabular-nums">
        {formatCalendarDay(date)}
      </span>
    </span>
  )
}

function ResultPanel({
  result,
  locale,
  calendarMode,
  onUseWeekendsOnly,
}: {
  result: BusinessDaysCalculation | null
  locale: string
  calendarMode: CalendarMode
  onUseWeekendsOnly: () => void
}) {
  const t = useT()

  if (!result) {
    return (
      <div className="flex flex-col justify-between gap-6 bg-secondary/30 p-4 sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:min-h-[360px]">
        <div>
          <p className="text-sm font-semibold text-primary">{t.businessDays.resultReady}</p>
          <h2 className="mt-3 text-2xl font-bold tracking-normal">
            {t.businessDays.emptyResultTitle}
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.businessDays.emptyResultDescription}
          </p>
        </div>
      </div>
    )
  }

  if (result.kind === 'coverage-error') {
    return (
      <div className="flex flex-col justify-between gap-6 bg-secondary/30 p-4 sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:min-h-[360px]">
        <div>
          <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
            {t.businessDays.coverageErrorEyebrow}
          </p>
          <h2 className="mt-3 text-2xl font-bold tracking-normal">
            {t.businessDays.coverageErrorTitle}
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.businessDays.coverageErrorDescription.replace(
              '{years}',
              result.missingYears.join(', '),
            )}
          </p>
          <p className="mt-3 rounded-md border border-amber-300/70 bg-amber-100/70 px-3 py-2 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200">
            {t.businessDays.noFallback}
          </p>
        </div>
        <Button type="button" onClick={onUseWeekendsOnly}>
          {t.businessDays.useWeekendsOnly}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 bg-secondary/30 p-4 sm:space-y-5 sm:p-6 lg:col-start-2 lg:row-span-2 lg:row-start-1">
      <div>
        <p className="text-sm font-semibold text-primary">{t.businessDays.resultReady}</p>
        <div className="mt-3 flex items-end gap-3">
          <span className="text-5xl font-bold leading-none tabular-nums tracking-normal sm:text-6xl">
            {result.workingDays}
          </span>
          <span className="pb-1 text-base font-medium text-muted-foreground">
            {t.businessDays.workingDaysLower}
          </span>
        </div>
        {result.swapped && (
          <p className="mt-3 rounded-md border border-amber-300/70 bg-amber-100/70 px-3 py-2 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200">
            {t.businessDays.swapped}
          </p>
        )}
      </div>

      <div className="hidden rounded-lg border bg-card/80 p-4 shadow-sm sm:block">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold tracking-normal">{t.businessDays.timeline}</h2>
          <span className="text-xs text-muted-foreground">
            {formatIsoDate(result.rangeStart, locale)} - {formatIsoDate(result.rangeEnd, locale)}
          </span>
        </div>
        <DateRangeRail result={result} />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Metric label={t.businessDays.calendarDays} value={result.calendarDays} />
        <Metric label={t.businessDays.workingDays} value={result.workingDays} accent />
        <Metric
          label={t.businessDays.ordinaryWeekendDays}
          value={result.ordinaryWeekendDays}
        />
        <Metric
          label={t.businessDays.federalHolidayDays}
          value={result.federalHolidayOrTransferDays}
        />
      </div>

      <p className="text-xs leading-5 text-muted-foreground">
        {calendarMode === 'weekends_only'
          ? t.businessDays.noHolidayGuarantee
          : t.businessDays.federalGuardrail}
      </p>

      {result.coveredCalendars.map((calendar) => (
        <article key={calendar.year} className="rounded-lg border bg-card/80 p-4 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="font-semibold">
                {t.businessDays.coverageYear.replace('{year}', String(calendar.year))}
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                {t.businessDays.coverageMetadata
                  .replace('{status}', calendar.status)
                  .replace('{date}', calendar.lastVerifiedAt)}
              </p>
            </div>
            <a
              href={productionCalendarYearRoute(calendar.year)}
              className="text-sm font-medium text-primary hover:underline"
            >
              {t.businessDays.openCalendarYear}
            </a>
          </div>
          <ul className="mt-3 space-y-1 text-xs leading-5 text-muted-foreground">
            {calendar.sources.filter((source) => source.kind === 'resolution').map((source) => (
              <li key={source.id}>
                <a
                  href={source.url}
                  rel="noreferrer"
                  target="_blank"
                  className="underline decoration-border underline-offset-2 hover:text-foreground"
                >
                  {source.title}
                </a>
              </li>
            ))}
          </ul>
        </article>
      ))}
    </div>
  )
}

function formatIsoDate(value: string, locale: string): string {
  return formatDate(new Date(`${value}T00:00:00`), locale)
}

function DateRangeRail({ result }: { result: BusinessDaysResult }) {
  const segmentCount = Math.max(1, Math.min(28, result.calendarDays || 1))
  const totalDays = Math.max(1, result.calendarDays)
  const segments = Array.from({ length: segmentCount }, (_, index) => {
    const offset = Math.min(
      totalDays - 1,
      Math.floor((index / Math.max(1, segmentCount - 1)) * (totalDays - 1)),
    )

    const start = new Date(`${result.rangeStart}T00:00:00`)
    const day = addDays(start, offset).getDay()
    return { weekend: day === 0 || day === 6 }
  })

  return (
    <div className="flex items-center gap-1" aria-hidden="true">
      {segments.map((segment, index) => (
        <span
          key={index}
          className={cn(
            'h-3 flex-1 rounded-full',
            segment.weekend ? 'bg-amber-300/80 dark:bg-amber-400/50' : 'bg-primary/45',
          )}
        />
      ))}
    </div>
  )
}

function Metric({
  label,
  value,
  accent = false,
}: {
  label: string
  value: string | number
  accent?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2.5',
        accent ? 'bg-accent text-accent-foreground' : 'bg-background/70',
      )}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums tracking-normal">{value}</p>
    </div>
  )
}

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <div>
      <dt className="font-medium">{q}</dt>
      <dd className="mt-1 text-sm leading-6 text-muted-foreground">{a}</dd>
    </div>
  )
}
